import React, { useCallback, useMemo, useRef, useState } from 'react';
import Immutable from 'immutable';
import classnames from 'classnames';

import type { VoiceRow } from './types';

import type { AIVoice } from 'js/src/client';
import {
    BUTTON_SIZES,
    IconButton,
    MediaIconButton,
    SecondaryIconButton,
} from 'js/src/components/common/ui/Button';
import type { DropdownItemProp, DropdownRef } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';
import UpsellIcon from 'js/src/components/common/ui/UpsellIcon';
import type { VOICE_SELECTION_MODAL_SOURCE } from 'js/src/data/modals';
import { DROPDOWN_ITEM_TEXT_STYLES, ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';

export type AudioElementRefsType = React.MutableRefObject<Record<string, HTMLAudioElement>>;

const AIVoiceAttributeValue = ({ values }: { values: string[] }) => {
    const joined = values.join(', ');
    const content = <>{joined || <>&ndash;</>}</>;
    if (joined) {
        return (
            <Tooltip title={joined}>
                <span className="d-inline-block mw-100 align-middle text-truncate">{content}</span>
            </Tooltip>
        );
    }
    return content;
};

const AIVoiceLanguageValue = ({
    languages,
    accents,
    selectedLanguageDisplay,
}: {
    languages: string[];
    accents: string[];
    selectedLanguageDisplay: string | null;
}) => {
    if (languages.length === 0) {
        return <>&ndash;</>;
    }

    const primaryLanguage =
        (selectedLanguageDisplay &&
            languages.find((language) => language === selectedLanguageDisplay)) ||
        languages[0];
    const additionalLanguageCount = languages.length - 1;

    const tooltipContent = (
        <div className="text-start">
            <div>{languages.join(', ')}</div>
            {accents.length > 0 && <div className="mt-l5-4 opacity-75">{accents.join(', ')}</div>}
        </div>
    );

    return (
        <Tooltip title={tooltipContent}>
            <span className="d-inline-flex align-items-center mw-100 align-middle">
                <span className="text-truncate">{primaryLanguage}</span>
                {additionalLanguageCount > 0 && (
                    <span className="ms-l5-4 flex-shrink-0 color-grey-600">
                        +{additionalLanguageCount}
                    </span>
                )}
            </span>
        </Tooltip>
    );
};

const KEBAB_TOGGLE_PROPS = Immutable.fromJS({
    icon: 'more_vert',
    size: BUTTON_SIZES.SM,
    className: 'p-l5-0',
});

enum VoiceRowAction {
    FAVORITE = 'favorite',
    EDIT = 'edit',
    DELETE = 'delete',
}

type AIVoiceRowProps = {
    className?: string;
    data: VoiceRow;
    style: React.CSSProperties;
    isSelected: boolean;
    onVoiceSelected: (voice: AIVoice) => void;
    isPreviewing: boolean;
    stopPreview: () => void;
    startPreview: (slug: string) => void;
    canUsePremiumAIVoices: boolean;
    containerRef: React.MutableRefObject<HTMLDivElement | null>;
    registerAudioElement: (slug: string, el: HTMLAudioElement, fallbackUrl: string | null) => void;
    source: VOICE_SELECTION_MODAL_SOURCE | null;
    onToggleFavourite: (slug: string) => void;
    onEditVoice?: (slug: string) => void;
    onDeleteVoice?: (slug: string) => void;
    selectedLanguage: string | null;
    selectedLanguageDisplay: string | null;
};

const AIVoiceRow: React.FC<AIVoiceRowProps> = ({
    data,
    style,
    className,
    isSelected,
    onVoiceSelected,
    isPreviewing,
    stopPreview,
    startPreview,
    canUsePremiumAIVoices,
    containerRef,
    registerAudioElement,
    source,
    onToggleFavourite,
    onEditVoice,
    onDeleteVoice,
    selectedLanguage,
    selectedLanguageDisplay,
}) => {
    const {
        slug,
        display_name: displayName,
        is_paid: isPaid,
        is_private: isPrivate,
        is_deprecated: isDeprecated,
        is_favourited: isFavourited,
        preview_url: previewUrl,
        preview_urls: previewUrls,
        attributes,
    } = data.voice;

    const effectivePreviewUrl = (selectedLanguage && previewUrls[selectedLanguage]) || previewUrl;

    const kebabRef = useRef<DropdownRef | null>(null);
    const [isKebabOpen, setIsKebabOpen] = useState(false);

    const audioCallbackRef = useCallback(
        (el: HTMLAudioElement | null) => {
            if (el) {
                registerAudioElement(slug, el, previewUrl);
            }
        },
        [registerAudioElement, slug, previewUrl],
    );

    const previewIcon = isPreviewing ? 'stop' : 'play_arrow';

    const selectVoice = useCallback(
        (e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest('[data-ai-voice-row-kebab]')) return;
            onVoiceSelected(data.voice);
        },
        [onVoiceSelected, data.voice],
    );

    const handleToggleFavourite = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onToggleFavourite(slug);
        },
        [onToggleFavourite, slug],
    );

    const togglePlayPause = useCallback(() => {
        if (isPreviewing) {
            stopPreview();
        } else {
            startPreview(slug);
            analytics.track('Voice previewed', {
                voice: displayName,
                kind: isPaid ? 'premium' : 'free',
                source: source,
            });
        }
    }, [isPreviewing, stopPreview, startPreview, slug, displayName, isPaid, source]);

    type KebabSelectData = { action: VoiceRowAction };

    const kebabItems = useMemo(() => {
        if (!isPrivate) return Immutable.List<DropdownItemProp<KebabSelectData>>();
        return ImmutableFromJS([
            {
                id: VoiceRowAction.EDIT,
                text: 'Edit',
                onSelectData: { action: VoiceRowAction.EDIT },
            },
            {
                id: VoiceRowAction.FAVORITE,
                text: isFavourited ? 'Remove from Favorites' : 'Add to Favorites',
                onSelectData: { action: VoiceRowAction.FAVORITE },
            },
            {
                id: VoiceRowAction.DELETE,
                text: 'Delete',
                textStyle: DROPDOWN_ITEM_TEXT_STYLES.DANGER,
                onSelectData: { action: VoiceRowAction.DELETE },
            },
        ]) as Immutable.List<DropdownItemProp<KebabSelectData>>;
    }, [isPrivate, isFavourited]);

    const handleKebabSelect = useCallback(
        (selectedOption: ImmutableMap<KebabSelectData>) => {
            const action = selectedOption.toJS().action;
            switch (action) {
                case VoiceRowAction.FAVORITE: {
                    onToggleFavourite(slug);

                    break;
                }
                case VoiceRowAction.EDIT: {
                    onEditVoice?.(slug);

                    break;
                }
                case VoiceRowAction.DELETE: {
                    onDeleteVoice?.(slug);

                    break;
                }
                // No default
            }
        },
        [slug, onToggleFavourite, onEditVoice, onDeleteVoice],
    );

    const handleKebabAfterOpen = useCallback(() => setIsKebabOpen(true), []);
    const handleKebabAfterClose = useCallback(() => setIsKebabOpen(false), []);

    return (
        <div
            className={classnames('row cursor-pointer', className, {
                selected: isSelected,
            })}
            style={isKebabOpen ? { ...style, position: 'relative', zIndex: 1 } : style}
            onClick={selectVoice}
            data-testid="ai-voice-row"
        >
            <div className="col-5 col-voice color-grey-900 d-flex flex-row align-items-center">
                <audio
                    src={effectivePreviewUrl || undefined}
                    preload="none"
                    ref={audioCallbackRef}
                />
                <IconButton
                    className={classnames(
                        'material-symbols-f1 border-0 preview-button flex-shrink-0',
                        {
                            playing: isPreviewing,
                        },
                    )}
                    onClick={togglePlayPause}
                    size={BUTTON_SIZES.SM}
                    icon={previewIcon}
                />
                <Tooltip title={displayName}>
                    <div className="ms-l5-8 min-width-0 text-truncate">{displayName}</div>
                </Tooltip>
                {isDeprecated && (
                    <Tooltip
                        title="Legacy voices may be less consistent in tone and may experience more audio anomalies."
                        container={containerRef}
                        placement={Placement.RIGHT}
                    >
                        <div className="flex-shrink-0 ms-l5-4">
                            <span translate="no" className="material-symbols-outlined">
                                info
                            </span>
                        </div>
                    </Tooltip>
                )}
                {!canUsePremiumAIVoices && isPaid && (
                    <Tooltip
                        title="Upgrade to the Basic plan or above to apply this voice"
                        container={containerRef}
                        placement={Placement.RIGHT}
                    >
                        <div className="flex-shrink-0 ms-l5-4">
                            <UpsellIcon isOutlined />
                        </div>
                    </Tooltip>
                )}
            </div>
            <div className="col">
                <AIVoiceLanguageValue
                    languages={attributes.language}
                    accents={attributes.accent}
                    selectedLanguageDisplay={selectedLanguageDisplay}
                />
            </div>
            <div className="col">
                <AIVoiceAttributeValue values={attributes.tone} />
            </div>
            <div className="col">
                <AIVoiceAttributeValue values={attributes.style} />
            </div>
            <div className="col-auto">
                {isPrivate && onEditVoice && onDeleteVoice ? (
                    <div data-ai-voice-row-kebab>
                        <Dropdown
                            myRef={kebabRef}
                            myMenuPath={`voice-kebab-${slug}`}
                            ToggleComponent={SecondaryIconButton}
                            toggleProps={KEBAB_TOGGLE_PROPS}
                            itemPropsList={kebabItems}
                            onSelect={handleKebabSelect}
                            afterOpen={handleKebabAfterOpen}
                            afterClose={handleKebabAfterClose}
                            breakOverflow
                        />
                    </div>
                ) : (
                    <MediaIconButton
                        className={classnames('favourite-button', {
                            'material-symbols-f1': isFavourited,
                        })}
                        icon="star"
                        onClick={handleToggleFavourite}
                        size={BUTTON_SIZES.SM}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(AIVoiceRow);
